import React, { useEffect, useState } from 'react';
import TheatreServices from '../services/externalServices/TicketServices/theatreServices';
import PricingConfigServices from '../services/externalServices/TicketServices/pricingConfigServices';

const Tab = ({ label, active, onClick }) => (
  <button className={`px-4 py-2 rounded ${active ? 'bg-black text-white' : 'bg-gray-200 text-black'}`} onClick={onClick}>{label}</button>
);

const PricingConfig = () => {
  const [activeTab, setActiveTab] = useState('Gateway Fees');
  const tabs = ['Payment Gateways', 'Base GST', 'Gateway Fees', 'Platform Fees', 'Convenience Fees', 'Discounts', 'Coupons', 'Redemptions', 'Preview'];
  const [serviceType, setServiceType] = useState('Tickets');
  const [serviceId, setServiceId] = useState('');
  const [theatres, setTheatres] = useState([]);
  const [gatewayFees, setGatewayFees] = useState([]);
  const [platformFees, setPlatformFees] = useState([]);
  const [convenienceFees, setConvenienceFees] = useState([]);
  const [discountRules, setDiscountRules] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [form, setForm] = useState({ gateway: '', paymentMethod: 'All', feeType: 'Flat', feeValue: 0, mode: 'EXCLUSIVE', gstType: 'Percent', gstState: 'State', gstValue: 0, validFrom: '', validTill: '', active: true });
  const [gatewayMaster, setGatewayMaster] = useState([]);
  const [newGateway, setNewGateway] = useState({ name: '', code: '', active: true });
  const [baseGstRules, setBaseGstRules] = useState([]);
  const [newBaseGst, setNewBaseGst] = useState({ minAmount: 0, maxAmount: 100, ratePercent: 12, gstState: 'State', validFrom: '', validTill: '', active: true });
  const [previewTickets, setPreviewTickets] = useState([{ section: 'Default', price: 100, count: 1 }]);
  const [previewGateway, setPreviewGateway] = useState('');
  const [previewMethod, setPreviewMethod] = useState('All');
  const [previewCoupon, setPreviewCoupon] = useState('');
  const [previewResult, setPreviewResult] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState('');
  const [editItem, setEditItem] = useState(null);

  // Default valid dates to today and +5 years
  useEffect(() => {
    const today = new Date();
    const plus5 = new Date(); plus5.setFullYear(today.getFullYear() + 5);
    setForm(f => ({ ...f, validFrom: f.validFrom || today.toISOString().slice(0,10), validTill: f.validTill || plus5.toISOString().slice(0,10) }));
  }, []);

  useEffect(() => {
    if (serviceType === 'Tickets') {
      TheatreServices.listTheatres().then(res => {
        const list = Array.isArray(res) ? res : res?.data || [];
        setTheatres(list);
      }).catch(()=> setTheatres([]));
    }
    // Load gateway master list
    PricingConfigServices.listGatewaysMaster().then(res => setGatewayMaster(res?.data || [])).catch(()=> setGatewayMaster([]));
  }, [serviceType]);

  useEffect(() => {
    if (!serviceType || !serviceId) { setGatewayFees([]); return; }
    PricingConfigServices.listGatewayFees({ serviceType, serviceId }).then(res => {
      setGatewayFees(res?.data || []);
    }).catch(()=> setGatewayFees([]));
  }, [serviceType, serviceId, activeTab]);

  // Load data per active tab
  useEffect(() => {
    if (!serviceType || !serviceId) {
      setPlatformFees([]); setConvenienceFees([]); setDiscountRules([]); setCoupons([]); setRedemptions([]);
      return;
    }
    if (activeTab === 'Platform Fees') {
      PricingConfigServices.listPlatformFees({ serviceType, serviceId }).then(res => setPlatformFees(res?.data || [])).catch(()=> setPlatformFees([]));
    }
    if (activeTab === 'Convenience Fees') {
      PricingConfigServices.listConvenienceFees({ serviceType, serviceId }).then(res => setConvenienceFees(res?.data || [])).catch(()=> setConvenienceFees([]));
    }
    if (activeTab === 'Discounts') {
      PricingConfigServices.listDiscountRules({ serviceType, serviceId }).then(res => setDiscountRules(res?.data || [])).catch(()=> setDiscountRules([]));
    }
    if (activeTab === 'Coupons') {
      PricingConfigServices.listCoupons({ serviceType, serviceId }).then(res => setCoupons(res?.data || [])).catch(()=> setCoupons([]));
    }
    if (activeTab === 'Redemptions') {
      PricingConfigServices.listRedemptions({ serviceType, serviceId }).then(res => setRedemptions(res?.data || [])).catch(()=> setRedemptions([]));
    }
  }, [activeTab, serviceType, serviceId]);

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">Pricing Configuration</h2>
      <p className="text-sm text-gray-600 mb-4">Master & Config → Master → Pricing Config</p>
      <div className="flex gap-2 mb-6 flex-wrap">
        {tabs.map(t => <Tab key={t} label={t} active={activeTab === t} onClick={()=>setActiveTab(t)} />)}
      </div>
      {/* Filters */}
      <div className="border rounded p-4 bg-white mb-4">
        <div className="flex gap-4 items-end flex-wrap">
          <div>
            <label className="block text-sm mb-1">Service Type</label>
            <select className="border rounded px-3 py-2" value={serviceType} onChange={e=>{ setServiceType(e.target.value); setServiceId(''); }}>
              {['Tickets','Taxi','Auto','Tourpackage','Tracking'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm mb-1">Service ID</label>
            {serviceType === 'Tickets' ? (
              <select className="border rounded px-3 py-2" value={serviceId} onChange={e=> setServiceId(e.target.value)}>
                <option value="">Select Theatre</option>
                {theatres.map(th => <option key={th._id} value={th._id}>{th.name}</option>)}
              </select>
            ) : (
              <input className="border rounded px-3 py-2" placeholder="Enter Service ID (placeholder)" value={serviceId} onChange={e=> setServiceId(e.target.value)} />
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="border rounded p-4 bg-white">
        {activeTab === 'Payment Gateways' && (
          <div className="mb-2">
            <h3 className="font-semibold mb-3">Payment Gateways (Master)</h3>
            <div className="overflow-x-auto mb-3">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left">
                    {['Name','Code','Active','Actions'].map(h => <th key={h} className="px-2 py-1 border-b">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {gatewayMaster.map(g => (
                    <tr key={g._id}>
                      <td className="px-2 py-1 border-b">{g.name}</td>
                      <td className="px-2 py-1 border-b">{g.code}</td>
                      <td className="px-2 py-1 border-b">{g.active ? 'Yes' : 'No'}</td>
                      <td className="px-2 py-1 border-b">
                        <button className="text-blue-600 mr-2" onClick={()=>{ setModalOpen(true); setModalTab('Payment Gateways'); setEditItem(g); }}>Edit</button>
                        <button className="text-red-600" onClick={async ()=>{ await PricingConfigServices.deleteGatewayMaster(g._id); const res = await PricingConfigServices.listGatewaysMaster(); setGatewayMaster(res?.data||[]); }}>Delete</button>
                      </td>
                    </tr>
                  ))}
                  {gatewayMaster.length === 0 && <tr><td className="px-2 py-2 text-gray-500" colSpan={4}>No gateways</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-sm mb-1">Name</label>
                <input className="border rounded px-3 py-2" value={newGateway.name} onChange={e=> setNewGateway({...newGateway, name: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm mb-1">Code</label>
                <input className="border rounded px-3 py-2" value={newGateway.code} onChange={e=> setNewGateway({...newGateway, code: e.target.value})} />
              </div>
              <div className="flex items-center gap-2 mt-6">
                <input type="checkbox" checked={newGateway.active} onChange={e=> setNewGateway({...newGateway, active: e.target.checked})} />
                <span>Active</span>
              </div>
              <div className="mt-6">
                <button className="px-4 py-2 bg-black text-white rounded" onClick={async ()=>{
                  if (!newGateway.name || !newGateway.code) { alert('Enter name and code'); return; }
                  await PricingConfigServices.createGatewayMaster(newGateway);
                  const res = await PricingConfigServices.listGatewaysMaster();
                  setGatewayMaster(res?.data || []);
                  setNewGateway({ name: '', code: '', active: true });
                }}>Add Gateway</button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'Base GST' && (
          <div className="mb-2">
            <h3 className="font-semibold mb-3">Base Fare GST Brackets</h3>
            <div className="overflow-x-auto mb-3">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left">
                    {['Range','Rate %','State','Validity','Active','Actions'].map(h => <th key={h} className="px-2 py-1 border-b">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {baseGstRules.map(r => (
                    <tr key={r._id}>
                      <td className="px-2 py-1 border-b">{r.minAmount} - {r.maxAmount}</td>
                      <td className="px-2 py-1 border-b">{r.ratePercent}%</td>
                      <td className="px-2 py-1 border-b">{r.gstState}</td>
                      <td className="px-2 py-1 border-b">{new Date(r.validFrom).toLocaleDateString()} → {new Date(r.validTill).toLocaleDateString()}</td>
                      <td className="px-2 py-1 border-b">{r.active ? 'Yes' : 'No'}</td>
                      <td className="px-2 py-1 border-b"><button className="text-red-600" onClick={async ()=>{ await PricingConfigServices.deleteBaseGst(r._id); const res = await PricingConfigServices.listBaseGst({ serviceType, serviceId }); setBaseGstRules(res?.data||[]); }}>Delete</button></td>
                    </tr>
                  ))}
                  {baseGstRules.length === 0 && <tr><td className="px-2 py-2 text-gray-500" colSpan={6}>No GST brackets</td></tr>}
                </tbody>
              </table>
            </div>
            <h4 className="font-medium mb-2">Add GST Bracket</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm mb-1">Min</label>
                <input className="border rounded px-3 py-2" type="number" value={newBaseGst.minAmount} onChange={e=> setNewBaseGst({...newBaseGst, minAmount: Number(e.target.value)})} />
              </div>
              <div>
                <label className="block text-sm mb-1">Max</label>
                <input className="border rounded px-3 py-2" type="number" value={newBaseGst.maxAmount} onChange={e=> setNewBaseGst({...newBaseGst, maxAmount: Number(e.target.value)})} />
              </div>
              <div>
                <label className="block text-sm mb-1">Rate %</label>
                <input className="border rounded px-3 py-2" type="number" value={newBaseGst.ratePercent} onChange={e=> setNewBaseGst({...newBaseGst, ratePercent: Number(e.target.value)})} />
              </div>
              <div>
                <label className="block text-sm mb-1">GST State</label>
                <select className="border rounded px-3 py-2" value={newBaseGst.gstState} onChange={e=> setNewBaseGst({...newBaseGst, gstState: e.target.value})}>
                  {['Integrated','State'].map(x=> <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Valid From</label>
                <input className="border rounded px-3 py-2" type="date" value={newBaseGst.validFrom} onChange={e=> setNewBaseGst({...newBaseGst, validFrom: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm mb-1">Valid Till</label>
                <input className="border rounded px-3 py-2" type="date" value={newBaseGst.validTill} onChange={e=> setNewBaseGst({...newBaseGst, validTill: e.target.value})} />
              </div>
              <div className="flex items-center gap-2 mt-6">
                <input type="checkbox" checked={newBaseGst.active} onChange={e=> setNewBaseGst({...newBaseGst, active: e.target.checked})} />
                <span>Active</span>
              </div>
              <div className="mt-6">
                <button className="px-4 py-2 bg-black text-white rounded" onClick={async ()=>{
                  if (!serviceType || !serviceId) { alert('Select Service Type and Service ID'); return; }
                  await PricingConfigServices.createBaseGst({ ...newBaseGst, serviceType, serviceId });
                  const res = await PricingConfigServices.listBaseGst({ serviceType, serviceId }); setBaseGstRules(res?.data || []);
                  setNewBaseGst({ minAmount: 0, maxAmount: 100, ratePercent: 12, gstState: 'State', validFrom: '', validTill: '', active: true });
                }}>Add Bracket</button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'Gateway Fees' && (
          <div>
            <h3 className="font-semibold mb-3">Gateway Fee Rules</h3>
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left">
                    {['Gateway','Method','PG Mode','Fee','GST','Service','Service ID','Validity','Active','Actions'].map(h => <th key={h} className="px-2 py-1 border-b">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {gatewayFees.map(rule => (
                    <tr key={rule._id}>
                      <td className="px-2 py-1 border-b">{rule.gateway}</td>
                      <td className="px-2 py-1 border-b">{rule.paymentMethod}</td>
                      <td className="px-2 py-1 border-b">{rule.mode}</td>
                      <td className="px-2 py-1 border-b">{rule.feeType} {rule.feeValue}</td>
                      <td className="px-2 py-1 border-b">{rule.gstState} {rule.gstType} {rule.gstValue}</td>
                      <td className="px-2 py-1 border-b">{rule.serviceType || '-'}</td>
                      <td className="px-2 py-1 border-b">{
                        (()=>{
                          if (rule.serviceType === 'Tickets') {
                            const th = theatres.find(t=> String(t._id) === String(rule.serviceId));
                            return th ? th.name : String(rule.serviceId);
                          }
                          return String(rule.serviceId||'');
                        })()
                      }</td>
                      <td className="px-2 py-1 border-b">{new Date(rule.validFrom).toLocaleDateString()} → {new Date(rule.validTill).toLocaleDateString()}</td>
                      <td className="px-2 py-1 border-b">{rule.active ? 'Yes' : 'No'}</td>
                      <td className="px-2 py-1 border-b">
                        <button className="text-blue-600 mr-2" onClick={()=>{ setModalOpen(true); setModalTab('Gateway Fees'); setEditItem(rule); }}>Edit</button>
                        <button className="text-red-600" onClick={async ()=>{ await PricingConfigServices.deleteGatewayFee(rule._id); const res = await PricingConfigServices.listGatewayFees({ serviceType, serviceId }); setGatewayFees(res?.data||[]); }}>Delete</button>
                      </td>
                    </tr>
                  ))}
                  {gatewayFees.length === 0 && (
                    <tr><td className="px-2 py-2 text-gray-500" colSpan={10}>No rules found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <h4 className="font-medium mb-2">Add New Rule</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm mb-1">Gateway</label>
                <select className="border rounded px-3 py-2" value={form.gateway} onChange={e=> setForm({...form, gateway: e.target.value})}>
                  <option value="">Select</option>
                  {gatewayMaster.filter(g=>g.active).map(g => <option key={g._id} value={g.name}>{g.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">PG Mode</label>
                <select className="border rounded px-3 py-2" value={form.mode} onChange={e=> setForm({...form, mode: e.target.value})}>
                  {['EXCLUSIVE','INCLUSIVE'].map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Method</label>
                <select className="border rounded px-3 py-2" value={form.paymentMethod} onChange={e=> setForm({...form, paymentMethod: e.target.value})}>
                  {['All','Credit Card','Debit Card','UPI','Netbanking'].map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Fee Type</label>
                <select className="border rounded px-3 py-2" value={form.feeType} onChange={e=> setForm({...form, feeType: e.target.value})}>
                  {['Flat','Percent'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Fee Value</label>
                <input className="border rounded px-3 py-2" type="number" value={form.feeValue} onChange={e=> setForm({...form, feeValue: Number(e.target.value)})} />
              </div>
              <div>
                <label className="block text-sm mb-1">GST Type</label>
                <select className="border rounded px-3 py-2" value={form.gstType} onChange={e=> setForm({...form, gstType: e.target.value})}>
                  {['Flat','Percent'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">GST State</label>
                <select className="border rounded px-3 py-2" value={form.gstState} onChange={e=> setForm({...form, gstState: e.target.value})}>
                  {['Integrated','State'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">GST Value</label>
                <input className="border rounded px-3 py-2" type="number" value={form.gstValue} onChange={e=> setForm({...form, gstValue: Number(e.target.value)})} />
              </div>
              <div>
                <label className="block text-sm mb-1">Valid From</label>
                <input className="border rounded px-3 py-2" type="date" value={form.validFrom} onChange={e=> setForm({...form, validFrom: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm mb-1">Valid Till</label>
                <input className="border rounded px-3 py-2" type="date" value={form.validTill} onChange={e=> setForm({...form, validTill: e.target.value})} />
              </div>
              <div className="flex items-center gap-2 mt-6">
                <input type="checkbox" checked={form.active} onChange={e=> setForm({...form, active: e.target.checked})} />
                <span>Active</span>
              </div>
            </div>
            <div className="mt-3">
              <button
                className="px-4 py-2 bg-black text-white rounded"
                onClick={async ()=>{
                  if (!serviceType || !serviceId) { alert('Select Service Type and Service ID'); return; }
                  if (!form.validFrom || !form.validTill) { alert('Select validity dates'); return; }
                  try {
                    const payload = { ...form, serviceType, serviceId };
                    await PricingConfigServices.createGatewayFee(payload);
                    const res = await PricingConfigServices.listGatewayFees({ serviceType, serviceId });
                    setGatewayFees(res?.data || []);
                    setForm({ gateway: '', paymentMethod: 'All', feeType: 'Flat', feeValue: 0, gstType: 'Percent', gstState: 'State', gstValue: 0, validFrom: '', validTill: '', active: true });
                  } catch (e) {
                    alert('Failed to create rule');
                  }
                }}
              >Add Rule</button>
            </div>
          </div>
        )}

        {activeTab === 'Platform Fees' && (
          <div>
            <h3 className="font-semibold mb-3">Platform Fee Rules</h3>
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left">
                    {['Fee','GST','Validity','Active','Actions'].map(h => <th key={h} className="px-2 py-1 border-b">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {platformFees.map(rule => (
                    <tr key={rule._id}>
                      <td className="px-2 py-1 border-b">{rule.feeType} {rule.feeValue}</td>
                      <td className="px-2 py-1 border-b">{rule.gstState} {rule.gstRate}%</td>
                      <td className="px-2 py-1 border-b">{new Date(rule.validFrom).toLocaleDateString()} → {new Date(rule.validTill).toLocaleDateString()}</td>
                      <td className="px-2 py-1 border-b">{rule.active ? 'Yes' : 'No'}</td>
                      <td className="px-2 py-1 border-b">
                        <button className="text-blue-600 mr-2" onClick={()=>{ setModalOpen(true); setModalTab('Platform Fees'); setEditItem(rule); }}>Edit</button>
                        <button className="text-red-600" onClick={async ()=>{ await PricingConfigServices.deletePlatformFee(rule._id); const res = await PricingConfigServices.listPlatformFees({ serviceType, serviceId }); setPlatformFees(res?.data||[]); }}>Delete</button>
                      </td>
                    </tr>
                  ))}
                  {platformFees.length === 0 && (
                    <tr><td className="px-2 py-2 text-gray-500" colSpan={5}>No rules found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <h4 className="font-medium mb-2">Add New Rule</h4>
            <PlatformFeeForm onSubmit={async (payload)=>{ await PricingConfigServices.createPlatformFee({ ...payload, serviceType, serviceId }); const res = await PricingConfigServices.listPlatformFees({ serviceType, serviceId }); setPlatformFees(res?.data||[]); }} />
          </div>
        )}

        {activeTab === 'Convenience Fees' && (
          <div>
            <h3 className="font-semibold mb-3">Convenience Fee Rules</h3>
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left">
                    {['Fee','GST','Validity','Active','Actions'].map(h => <th key={h} className="px-2 py-1 border-b">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {convenienceFees.map(rule => (
                    <tr key={rule._id}>
                      <td className="px-2 py-1 border-b">{rule.feeType} {rule.feeValue}</td>
                      <td className="px-2 py-1 border-b">{rule.gstState} {rule.gstRate}%</td>
                      <td className="px-2 py-1 border-b">{new Date(rule.validFrom).toLocaleDateString()} → {new Date(rule.validTill).toLocaleDateString()}</td>
                      <td className="px-2 py-1 border-b">{rule.active ? 'Yes' : 'No'}</td>
                      <td className="px-2 py-1 border-b">
                        <button className="text-blue-600 mr-2" onClick={()=>{ setModalOpen(true); setModalTab('Convenience Fees'); setEditItem(rule); }}>Edit</button>
                        <button className="text-red-600" onClick={async ()=>{ await PricingConfigServices.deleteConvenienceFee(rule._id); const res = await PricingConfigServices.listConvenienceFees({ serviceType, serviceId }); setConvenienceFees(res?.data||[]); }}>Delete</button>
                      </td>
                    </tr>
                  ))}
                  {convenienceFees.length === 0 && (
                    <tr><td className="px-2 py-2 text-gray-500" colSpan={5}>No rules found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <h4 className="font-medium mb-2">Add New Rule</h4>
            <ConvenienceFeeForm onSubmit={async (payload)=>{ await PricingConfigServices.createConvenienceFee({ ...payload, serviceType, serviceId }); const res = await PricingConfigServices.listConvenienceFees({ serviceType, serviceId }); setConvenienceFees(res?.data||[]); }} />
          </div>
        )}

        {activeTab === 'Discounts' && (
          <div>
            <h3 className="font-semibold mb-3">Discount Rules (Generic)</h3>
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left">
                    {['Apply On','Type','Value/Cap','Validity','Active','Actions'].map(h => <th key={h} className="px-2 py-1 border-b">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {discountRules.map(rule => (
                    <tr key={rule._id}>
                      <td className="px-2 py-1 border-b">{rule.applyOn}</td>
                      <td className="px-2 py-1 border-b">{rule.type}</td>
                      <td className="px-2 py-1 border-b">{rule.value}{rule.type==='PercentWithCap' && ` / cap ${rule.cap}`}</td>
                      <td className="px-2 py-1 border-b">{new Date(rule.validFrom).toLocaleDateString()} → {new Date(rule.validTill).toLocaleDateString()}</td>
                      <td className="px-2 py-1 border-b">{rule.active ? 'Yes' : 'No'}</td>
                      <td className="px-2 py-1 border-b">
                        <button className="text-blue-600 mr-2" onClick={()=>{ setModalOpen(true); setModalTab('Discounts'); setEditItem(rule); }}>Edit</button>
                        <button className="text-red-600" onClick={async ()=>{ await PricingConfigServices.deleteDiscountRule(rule._id); const res = await PricingConfigServices.listDiscountRules({ serviceType, serviceId }); setDiscountRules(res?.data||[]); }}>Delete</button>
                      </td>
                    </tr>
                  ))}
                  {discountRules.length === 0 && (
                    <tr><td className="px-2 py-2 text-gray-500" colSpan={6}>No rules found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <h4 className="font-medium mb-2">Add New Rule</h4>
            <DiscountRuleForm onSubmit={async (payload)=>{ await PricingConfigServices.createDiscountRule({ ...payload, serviceType, serviceId }); const res = await PricingConfigServices.listDiscountRules({ serviceType, serviceId }); setDiscountRules(res?.data||[]); }} />
          </div>
        )}

        {activeTab === 'Coupons' && (
          <div>
            <h3 className="font-semibold mb-3">Coupons</h3>
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left">
                    {['Code','Apply On','Type','Val/Cap','Max Uses','Validity','Active','Actions'].map(h => <th key={h} className="px-2 py-1 border-b">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {coupons.map(c => (
                    <tr key={c._id}>
                      <td className="px-2 py-1 border-b">{c.code}</td>
                      <td className="px-2 py-1 border-b">{c.applyOn}</td>
                      <td className="px-2 py-1 border-b">{c.type}</td>
                      <td className="px-2 py-1 border-b">{c.value}{c.type==='PercentWithCap' && ` / cap ${c.cap}`}</td>
                      <td className="px-2 py-1 border-b">{c.maxUses||0}</td>
                      <td className="px-2 py-1 border-b">{new Date(c.validFrom).toLocaleDateString()} → {new Date(c.validTill).toLocaleDateString()}</td>
                      <td className="px-2 py-1 border-b">{c.active ? 'Yes' : 'No'}</td>
                      <td className="px-2 py-1 border-b">
                        <button className="text-blue-600 mr-2" onClick={()=>{ setModalOpen(true); setModalTab('Coupons'); setEditItem(c); }}>Edit</button>
                        <button className="text-red-600" onClick={async ()=>{ await PricingConfigServices.deleteCoupon(c._id); const res = await PricingConfigServices.listCoupons({ serviceType, serviceId }); setCoupons(res?.data||[]); }}>Delete</button>
                      </td>
                    </tr>
                  ))}
                  {coupons.length === 0 && (
                    <tr><td className="px-2 py-2 text-gray-500" colSpan={8}>No coupons</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <h4 className="font-medium mb-2">Add Coupon</h4>
            <CouponForm onSubmit={async (payload)=>{ await PricingConfigServices.createCoupon({ ...payload, serviceType, serviceId }); const res = await PricingConfigServices.listCoupons({ serviceType, serviceId }); setCoupons(res?.data||[]); }} />
          </div>
        )}

        {activeTab === 'Redemptions' && (
          <div>
            <h3 className="font-semibold mb-3">Coupon Redemptions (Read-only)</h3>
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left">
                    {['Code','Booking','Status','Created'].map(h => <th key={h} className="px-2 py-1 border-b">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {redemptions.map(r => (
                    <tr key={r._id}>
                      <td className="px-2 py-1 border-b">{r.code}</td>
                      <td className="px-2 py-1 border-b">{r.bookingId}</td>
                      <td className="px-2 py-1 border-b">{r.status}</td>
                      <td className="px-2 py-1 border-b">{new Date(r.createdAt).toLocaleString()}</td>
                    </tr>
                  ))}
                  {redemptions.length === 0 && (
                    <tr><td className="px-2 py-2 text-gray-500" colSpan={4}>No redemptions</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'Preview' && (
          <div>
            <h3 className="font-semibold mb-3">Preview Calculation (v2)</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div>
                <label className="block text-sm mb-1">Gateway</label>
                <select className="border rounded px-3 py-2" value={previewGateway} onChange={e=> setPreviewGateway(e.target.value)}>
                  <option value="">Select</option>
                  {gatewayMaster.filter(g=>g.active).map(g => <option key={g._id} value={g.name}>{g.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Method</label>
                <select className="border rounded px-3 py-2" value={previewMethod} onChange={e=> setPreviewMethod(e.target.value)}>
                  {['All','Credit Card','Debit Card','UPI','Netbanking'].map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Coupon (optional)</label>
                <input className="border rounded px-3 py-2" value={previewCoupon} onChange={e=> setPreviewCoupon(e.target.value.toUpperCase())} />
              </div>
            </div>
            <div className="mb-3">
              <h4 className="font-medium mb-2">Tickets</h4>
              {previewTickets.map((t,idx)=>(
                <div key={idx} className="flex gap-2 mb-2 items-center">
                  <input className="border rounded px-2 py-1 w-48" placeholder="Section" value={t.section} onChange={e=>{ const a=[...previewTickets]; a[idx].section=e.target.value; setPreviewTickets(a); }} />
                  <input className="border rounded px-2 py-1 w-28" type="number" placeholder="Price" value={t.price} onChange={e=>{ const a=[...previewTickets]; a[idx].price=Number(e.target.value); setPreviewTickets(a); }} />
                  <input className="border rounded px-2 py-1 w-20" type="number" placeholder="Count" value={t.count} onChange={e=>{ const a=[...previewTickets]; a[idx].count=Number(e.target.value); setPreviewTickets(a); }} />
                  <button className="text-red-600" onClick={()=>{ const a=[...previewTickets]; a.splice(idx,1); setPreviewTickets(a); }}>Remove</button>
                </div>
              ))}
              <button className="px-3 py-1 bg-gray-200 rounded" onClick={()=> setPreviewTickets([...previewTickets, { section: 'S', price: 100, count: 1 }])}>+ Add Row</button>
            </div>
            <div className="mt-3">
              <button className="px-4 py-2 bg-black text-white rounded" onClick={async ()=>{
                if (!serviceType || !serviceId) { alert('Select Service Type and Service ID'); return; }
                if (!previewGateway) { alert('Select Gateway'); return; }
                const ticketsObj = {};
                let amount = 0;
                previewTickets.forEach(t=>{ if(!t.section) return; ticketsObj[t.section] = { price: Number(t.price)||0, count: Number(t.count)||0 }; amount += (Number(t.price)||0) * (Number(t.count)||0); });
                try {
                  const res = await PricingConfigServices.calculatePricingV2({ amount, tickets: ticketsObj, serviceType, serviceId, theatreId: serviceId, gateway: previewGateway, paymentMethod: previewMethod, couponCode: previewCoupon||undefined });
                  setPreviewResult(res?.data || res);
                } catch(e) { alert('Preview failed'); setPreviewResult(null); }
              }}>Run Preview</button>
            </div>
            {previewResult && (
              <div className="mt-4">
                <table className="min-w-full text-sm mb-3">
                  <tbody>
                    {/* Base + GST lines */}
                    <tr><td className="px-2 py-1">Base Amount</td><td className="px-2 py-1">₹ {previewResult?.baseAmount?.value}</td></tr>
                    {Array.isArray(previewResult?.tax) && previewResult.tax.map((t,i)=>(
                      <tr key={i}><td className="px-2 py-1">{t.key}</td><td className="px-2 py-1">₹ {t.value}</td></tr>
                    ))}
                    {Number(previewResult?.discount?.value)||0 ? (
                      <tr>
                        <td className="px-2 py-1">{`${previewResult?.discount?.key}${previewResult?.discount?.config?.reason ? ` - ${previewResult.discount.config.reason}` : ''}`}</td>
                        <td className="px-2 py-1">₹ {previewResult?.discount?.value}</td>
                      </tr>
                    ) : null}

                    {/* Platform Fee section */}
                    <tr><td className="px-2 py-1">Platform Fee</td><td className="px-2 py-1">₹ {previewResult?.platformFee?.total?.value}</td></tr>
                    {Number(previewResult?.platformFee?.discount?.value)||0 ? (
                      <tr>
                        <td className="px-2 py-1">{`${previewResult?.platformFee?.discount?.key}${previewResult?.platformFee?.discount?.config?.reason ? ` - ${previewResult.platformFee.discount.config.reason}` : ''}`}</td>
                        <td className="px-2 py-1">₹ {previewResult?.platformFee?.discount?.value}</td>
                      </tr>
                    ) : null}

                    {/* Convenience Fee section */}
                    <tr><td className="px-2 py-1">Convenience Fee</td><td className="px-2 py-1">₹ {previewResult?.convenienceFee?.total?.value}</td></tr>
                    {Number(previewResult?.convenienceFee?.discount?.value)||0 ? (
                      <tr>
                        <td className="px-2 py-1">{`${previewResult?.convenienceFee?.discount?.key}${previewResult?.convenienceFee?.discount?.config?.reason ? ` - ${previewResult.convenienceFee.discount.config.reason}` : ''}`}</td>
                        <td className="px-2 py-1">₹ {previewResult?.convenienceFee?.discount?.value}</td>
                      </tr>
                    ) : null}

                    {/* Gateway Charges */}
                    <tr><td className="px-2 py-1">Gateway Charges</td><td className="px-2 py-1">₹ {previewResult?.gatewayCharges?.total?.value}</td></tr>

                    {/* Total Discount summary if present */}
                    {(() => {
                      const totalDisc = (Number(previewResult?.discount?.value)||0) + (Number(previewResult?.platformFee?.discount?.value)||0) + (Number(previewResult?.convenienceFee?.discount?.value)||0);
                      return totalDisc ? (
                        <tr><td className="px-2 py-1">Total Discount</td><td className="px-2 py-1">₹ {Math.abs(totalDisc)}</td></tr>
                      ) : null;
                    })()}

                    {/* Final */}
                    <tr><td className="px-2 py-1 font-semibold">Final Amount</td><td className="px-2 py-1 font-semibold">₹ {previewResult?.finalAmount?.value}</td></tr>
                  </tbody>
                </table>
                <pre className="bg-gray-100 p-3 rounded text-xs overflow-auto" style={{maxHeight:'400px'}}>{JSON.stringify(previewResult, null, 2)}</pre>
              </div>
            )}
          </div>
        )}
      </div>
      {modalOpen && (
        <EditModal tab={modalTab} item={editItem} onClose={()=>{ setModalOpen(false); setEditItem(null); }} onSaved={async ()=>{
          if (modalTab==='Payment Gateways') { const res = await PricingConfigServices.listGatewaysMaster(); setGatewayMaster(res?.data || []);} 
          if (modalTab==='Base GST') { const res = await PricingConfigServices.listBaseGst({ serviceType, serviceId }); setBaseGstRules(res?.data || []);} 
          if (modalTab==='Gateway Fees') { const res = await PricingConfigServices.listGatewayFees({ serviceType, serviceId }); setGatewayFees(res?.data || []);} 
          if (modalTab==='Platform Fees') { const res = await PricingConfigServices.listPlatformFees({ serviceType, serviceId }); setPlatformFees(res?.data || []);} 
          if (modalTab==='Convenience Fees') { const res = await PricingConfigServices.listConvenienceFees({ serviceType, serviceId }); setConvenienceFees(res?.data || []);} 
          if (modalTab==='Discounts') { const res = await PricingConfigServices.listDiscountRules({ serviceType, serviceId }); setDiscountRules(res?.data || []);} 
          if (modalTab==='Coupons') { const res = await PricingConfigServices.listCoupons({ serviceType, serviceId }); setCoupons(res?.data || []);} 
        }} />
      )}
    </div>
  );
};

export default PricingConfig;

function EditModal({ tab, item, onClose, onSaved }) {
  const [state, setState] = useState(item || {});
  const label = (k) => k;
  const renderField = (k) => {
    // Typed control for PG Mode when editing Gateway Fees
    if (tab === 'Gateway Fees' && k === 'mode') {
      return (
        <select className="border rounded px-3 py-2 w-full" value={state[k] || 'EXCLUSIVE'} onChange={e=> setState({ ...state, [k]: e.target.value })}>
          {['EXCLUSIVE','INCLUSIVE'].map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      );
    }
    // Payment method select
    if (k === 'paymentMethod') {
      return (
        <select className="border rounded px-3 py-2 w-full" value={state[k] || 'All'} onChange={e=> setState({ ...state, [k]: e.target.value })}>
          {['All','Credit Card','Debit Card','UPI','Netbanking'].map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      );
    }
    // Fee/GST type selects
    if (k === 'feeType' || k === 'gstType') {
      return (
        <select className="border rounded px-3 py-2 w-full" value={state[k] || 'Percent'} onChange={e=> setState({ ...state, [k]: e.target.value })}>
          {['Flat','Percent'].map(x => <option key={x} value={x}>{x}</option>)}
        </select>
      );
    }
    // GST state select
    if (k === 'gstState') {
      return (
        <select className="border rounded px-3 py-2 w-full" value={state[k] || 'State'} onChange={e=> setState({ ...state, [k]: e.target.value })}>
          {['Integrated','State'].map(x => <option key={x} value={x}>{x}</option>)}
        </select>
      );
    }
    // Discount applyOn
    if (k === 'applyOn') {
      return (
        <select className="border rounded px-3 py-2 w-full" value={state[k] || 'BaseFare'} onChange={e=> setState({ ...state, [k]: e.target.value })}>
          {['BaseFare','PlatformFee','ConvenienceFee'].map(x => <option key={x} value={x}>{x}</option>)}
        </select>
      );
    }
    // Discount type
    if (k === 'type') {
      return (
        <select className="border rounded px-3 py-2 w-full" value={state[k] || 'Flat'} onChange={e=> setState({ ...state, [k]: e.target.value })}>
          {['Flat','Percent','PercentWithCap'].map(x => <option key={x} value={x}>{x}</option>)}
        </select>
      );
    }
    // Active / enabled
    if (k === 'active' || k === 'isEnabled') {
      return (
        <div className="flex items-center gap-2">
          <input type="checkbox" checked={!!state[k]} onChange={e=> setState({ ...state, [k]: e.target.checked })} />
          <span>{k}</span>
        </div>
      );
    }
    // Date fields
    if (k === 'validFrom' || k === 'validTill') {
      const val = state[k] ? String(state[k]).slice(0,10) : '';
      return (
        <input className="border rounded px-3 py-2 w-full" type="date" value={val} onChange={e=> setState({ ...state, [k]: e.target.value })} />
      );
    }
    // Numeric controls
    if (['feeValue','gstValue','gstRate','minAmount','maxAmount','ratePercent','value','cap','maxUses'].includes(k)) {
      return (
        <input className="border rounded px-3 py-2 w-full" type="number" value={state[k] ?? ''} onChange={e=> setState({ ...state, [k]: e.target.value })} />
      );
    }
    // Fallback: generic input
    return (
      <input className="border rounded px-3 py-2 w-full" value={state[k] ?? ''} onChange={e=> setState({ ...state, [k]: e.target.value })} />
    );
  };
  const handleSave = async () => {
    try {
      const id = state._id;
      // Coerce simple numeric/boolean fields
      const numericKeys = ['feeValue','gstValue','gstRate','minAmount','maxAmount','ratePercent','value','cap','maxUses'];
      const booleanKeys = ['active','isEnabled'];
      const payload = { ...state };
      numericKeys.forEach(k => { if (payload[k] !== undefined && payload[k] !== '') payload[k] = Number(payload[k]); });
      booleanKeys.forEach(k => { if (payload[k] !== undefined) payload[k] = !!payload[k]; });
      if (tab==='Payment Gateways') await PricingConfigServices.updateGatewayMaster(id, state);
      if (tab==='Base GST') await PricingConfigServices.updateBaseGst(id, payload);
      if (tab==='Gateway Fees') await PricingConfigServices.updateGatewayFee(id, payload);
      if (tab==='Platform Fees') await PricingConfigServices.updatePlatformFee(id, payload);
      if (tab==='Convenience Fees') await PricingConfigServices.updateConvenienceFee(id, payload);
      if (tab==='Discounts') await PricingConfigServices.updateDiscountRule(id, payload);
      if (tab==='Coupons') await PricingConfigServices.updateCoupon(id, payload);
      onClose();
      onSaved && onSaved();
    } catch (e) {
      alert('Update failed');
    }
  };
  const skip = new Set(['__v','createdAt','updatedAt']);
  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white p-4 rounded w-full max-w-lg">
        <h3 className="font-semibold mb-3">Edit {tab.slice(0,-1)}</h3>
        <div className="grid grid-cols-1 gap-2 max-h-[60vh] overflow-auto pr-2">
          {Object.keys(state||{}).filter(k=>!skip.has(k)).map(k => (
            <div key={k}>
              <label className="block text-xs text-gray-600 mb-1">{label(k)}</label>
              {renderField(k)}
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <button className="px-3 py-1 bg-gray-200 rounded" onClick={onClose}>Cancel</button>
          <button className="px-3 py-1 bg-black text-white rounded" onClick={handleSave}>Save</button>
        </div>
      </div>
    </div>
  );
}

// Reusable small forms
function PlatformFeeForm({ onSubmit }) {
  const [state, setState] = useState({ feeType: 'Flat', feeValue: 0, gstState: 'State', gstRate: 18, validFrom: '', validTill: '', active: true });
  useEffect(() => {
    const today = new Date(); const plus5 = new Date(); plus5.setFullYear(today.getFullYear()+5);
    setState(s => ({ ...s, validFrom: s.validFrom || today.toISOString().slice(0,10), validTill: s.validTill || plus5.toISOString().slice(0,10) }));
  }, []);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div>
        <label className="block text-sm mb-1">Fee Type</label>
        <select className="border rounded px-3 py-2" value={state.feeType} onChange={e=> setState({...state, feeType: e.target.value})}>
          {['Flat','Percent'].map(x=> <option key={x} value={x}>{x}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-sm mb-1">Fee Value</label>
        <input className="border rounded px-3 py-2" type="number" value={state.feeValue} onChange={e=> setState({...state, feeValue: Number(e.target.value)})} />
      </div>
      <div>
        <label className="block text-sm mb-1">GST State</label>
        <select className="border rounded px-3 py-2" value={state.gstState} onChange={e=> setState({...state, gstState: e.target.value})}>
          {['Integrated','State'].map(x=> <option key={x} value={x}>{x}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-sm mb-1">GST Rate</label>
        <input className="border rounded px-3 py-2" type="number" value={state.gstRate} onChange={e=> setState({...state, gstRate: Number(e.target.value)})} />
      </div>
      <div>
        <label className="block text-sm mb-1">Valid From</label>
        <input className="border rounded px-3 py-2" type="date" value={state.validFrom} onChange={e=> setState({...state, validFrom: e.target.value})} />
      </div>
      <div>
        <label className="block text-sm mb-1">Valid Till</label>
        <input className="border rounded px-3 py-2" type="date" value={state.validTill} onChange={e=> setState({...state, validTill: e.target.value})} />
      </div>
      <div className="flex items-center gap-2 mt-6">
        <input type="checkbox" checked={state.active} onChange={e=> setState({...state, active: e.target.checked})} />
        <span>Active</span>
      </div>
      <div className="mt-6">
        <button className="px-4 py-2 bg-black text-white rounded" onClick={()=> onSubmit(state)}>Add Rule</button>
      </div>
    </div>
  );
}

function ConvenienceFeeForm({ onSubmit }) {
  return <PlatformFeeForm onSubmit={onSubmit} />;
}

function DiscountRuleForm({ onSubmit }) {
  const [state, setState] = useState({ applyOn: 'BaseFare', type: 'Flat', value: 0, cap: 0, validFrom: '', validTill: '', active: true });
  useEffect(() => {
    const today = new Date(); const plus5 = new Date(); plus5.setFullYear(today.getFullYear()+5);
    setState(s => ({ ...s, validFrom: s.validFrom || today.toISOString().slice(0,10), validTill: s.validTill || plus5.toISOString().slice(0,10) }));
  }, []);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div>
        <label className="block text-sm mb-1">Apply On</label>
        <select className="border rounded px-3 py-2" value={state.applyOn} onChange={e=> setState({...state, applyOn: e.target.value})}>
          {['BaseFare','PlatformFee','ConvenienceFee'].map(x=> <option key={x} value={x}>{x}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-sm mb-1">Type</label>
        <select className="border rounded px-3 py-2" value={state.type} onChange={e=> setState({...state, type: e.target.value})}>
          {['Flat','Percent','PercentWithCap'].map(x=> <option key={x} value={x}>{x}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-sm mb-1">Value</label>
        <input className="border rounded px-3 py-2" type="number" value={state.value} onChange={e=> setState({...state, value: Number(e.target.value)})} />
      </div>
      {state.type === 'PercentWithCap' && (
        <div>
          <label className="block text-sm mb-1">Cap</label>
          <input className="border rounded px-3 py-2" type="number" value={state.cap} onChange={e=> setState({...state, cap: Number(e.target.value)})} />
        </div>
      )}
      <div>
        <label className="block text-sm mb-1">Valid From</label>
        <input className="border rounded px-3 py-2" type="date" value={state.validFrom} onChange={e=> setState({...state, validFrom: e.target.value})} />
      </div>
      <div>
        <label className="block text-sm mb-1">Valid Till</label>
        <input className="border rounded px-3 py-2" type="date" value={state.validTill} onChange={e=> setState({...state, validTill: e.target.value})} />
      </div>
      <div className="flex items-center gap-2 mt-6">
        <input type="checkbox" checked={state.active} onChange={e=> setState({...state, active: e.target.checked})} />
        <span>Active</span>
      </div>
      <div className="mt-6">
        <button className="px-4 py-2 bg-black text-white rounded" onClick={()=> onSubmit(state)}>Add Rule</button>
      </div>
    </div>
  );
}

function CouponForm({ onSubmit }) {
  const [state, setState] = useState({ code: '', applyOn: 'BaseFare', type: 'Flat', value: 0, cap: 0, maxUses: 0, validFrom: '', validTill: '', active: true });
  useEffect(() => {
    const today = new Date(); const plus5 = new Date(); plus5.setFullYear(today.getFullYear()+5);
    setState(s => ({ ...s, validFrom: s.validFrom || today.toISOString().slice(0,10), validTill: s.validTill || plus5.toISOString().slice(0,10) }));
  }, []);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div>
        <label className="block text-sm mb-1">Code</label>
        <input className="border rounded px-3 py-2" value={state.code} onChange={e=> setState({...state, code: e.target.value.toUpperCase()})} />
      </div>
      <div>
        <label className="block text-sm mb-1">Apply On</label>
        <select className="border rounded px-3 py-2" value={state.applyOn} onChange={e=> setState({...state, applyOn: e.target.value})}>
          {['BaseFare','PlatformFee','ConvenienceFee'].map(x=> <option key={x} value={x}>{x}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-sm mb-1">Type</label>
        <select className="border rounded px-3 py-2" value={state.type} onChange={e=> setState({...state, type: e.target.value})}>
          {['Flat','Percent','PercentWithCap'].map(x=> <option key={x} value={x}>{x}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-sm mb-1">Value</label>
        <input className="border rounded px-3 py-2" type="number" value={state.value} onChange={e=> setState({...state, value: Number(e.target.value)})} />
      </div>
      {state.type === 'PercentWithCap' && (
        <div>
          <label className="block text-sm mb-1">Cap</label>
          <input className="border rounded px-3 py-2" type="number" value={state.cap} onChange={e=> setState({...state, cap: Number(e.target.value)})} />
        </div>
      )}
      <div>
        <label className="block text-sm mb-1">Max Uses</label>
        <input className="border rounded px-3 py-2" type="number" value={state.maxUses} onChange={e=> setState({...state, maxUses: Number(e.target.value)})} />
      </div>
      <div>
        <label className="block text-sm mb-1">Valid From</label>
        <input className="border rounded px-3 py-2" type="date" value={state.validFrom} onChange={e=> setState({...state, validFrom: e.target.value})} />
      </div>
      <div>
        <label className="block text-sm mb-1">Valid Till</label>
        <input className="border rounded px-3 py-2" type="date" value={state.validTill} onChange={e=> setState({...state, validTill: e.target.value})} />
      </div>
      <div className="flex items-center gap-2 mt-6">
        <input type="checkbox" checked={state.active} onChange={e=> setState({...state, active: e.target.checked})} />
        <span>Active</span>
      </div>
      <div className="mt-6">
        <button className="px-4 py-2 bg-black text-white rounded" onClick={()=> onSubmit(state)}>Add Coupon</button>
      </div>
    </div>
  );
}
